// compute area from space to display the tooltip
// use to define the best area around the anchor to display tooltip

type AreaData = { width: number; height: number; area: number };

export type Position = 'top' | 'right' | 'bottom' | 'left';
type Area = Partial<Record<Position, AreaData>>;

const getArea = (width: number, height: number): AreaData => ({
  width,
  height,
  area: width * height
});

const computeAreasAroundAnchor = (
  anchorRect: DOMRect,
  viewportHeight: number,
  constraintRectangle: DOMRect,
  topGap: number
): Area => ({
  // Available space above the anchor
  top: getArea(constraintRectangle.width, anchorRect.top - topGap),
  // Available space to the right of the anchor
  right: getArea(
    constraintRectangle.right - anchorRect.right,
    viewportHeight - topGap
  ),
  // Available space below the anchor
  bottom: getArea(
    constraintRectangle.width,
    viewportHeight - anchorRect.bottom
  ),
  // Available space to the left of the anchor
  left: getArea(
    anchorRect.left - constraintRectangle.left,
    viewportHeight - topGap
  )
});

const computeFinalTooltipPosition = (
  requestedPosition: Position | 'auto',
  areas: Area
): Position => {
  // If the developer explicitly requested a position, use it
  if (requestedPosition !== 'auto') return requestedPosition;

  // Otherwise, choose the position with the largest available area
  let positionCandidate: Position = 'top';
  for (const key in areas) {
    const k = key as Position;
    if ((areas[positionCandidate]?.area ?? 0) < (areas[k]?.area ?? 0)) {
      positionCandidate = k;
    }
  }
  return positionCandidate;
};

export default function positionTooltip(
  position: Position | 'auto',
  anchor: HTMLDivElement,
  elTooltip: HTMLDivElement,
  elTooltipArrow: HTMLDivElement,
  _elTooltipContainer: HTMLDivElement,
  parentConstraint: HTMLElement,
  topGap = 0
) {
  const { scrollX, scrollY, innerHeight: windowHeight } = window;

  const tooltipWidth = elTooltip.offsetWidth;
  const tooltipHeight = elTooltip.offsetHeight;
  let arrowX = 0;
  let arrowY = 0;
  const { offsetWidth: arrowWidth, offsetHeight: arrowHeight } = elTooltipArrow;

  const anchorRect = anchor.getBoundingClientRect();

  // Middle point of the anchor (used for centering tooltip and arrow)
  const anchorMiddleX = scrollX + anchorRect.left + anchorRect.width / 2;
  const anchorMiddleY = scrollY + anchorRect.top + anchorRect.height / 2;

  const constraintRectangle = parentConstraint.getBoundingClientRect();

  // Compute available space in each direction
  const areas = computeAreasAroundAnchor(
    anchorRect,
    windowHeight,
    constraintRectangle,
    topGap
  );

  // Decide tooltip position (requested vs. best fit)
  const finalPosition = computeFinalTooltipPosition(position, areas);

  let tooltipX = 0;
  let tooltipY = 0;

  switch (finalPosition) {
    case 'right':
      tooltipX = scrollX + anchorRect.right;
      tooltipY = anchorMiddleY - tooltipHeight / 2;
      elTooltip.classList.add('tooltip-right');
      // Arrow is vertically centered relative to the anchor
      arrowY = anchorMiddleY - tooltipY - arrowHeight / 2;
      break;

    case 'bottom':
      tooltipX = anchorMiddleX - tooltipWidth / 2;
      tooltipY = scrollY + anchorRect.bottom;
      elTooltip.classList.add('tooltip-bottom');
      // Arrow is horizontally centered relative to the anchor
      arrowX = anchorMiddleX - tooltipX - arrowWidth / 2;
      break;

    case 'left':
      tooltipX = scrollX + anchorRect.left - tooltipWidth;
      tooltipY = anchorMiddleY - tooltipHeight / 2;
      elTooltip.classList.add('tooltip-left');
      // Arrow is vertically centered relative to the anchor
      arrowY = anchorMiddleY - tooltipY - arrowHeight / 2;
      break;

    case 'top':
      tooltipX = anchorMiddleX - tooltipWidth / 2;
      tooltipY = scrollY + anchorRect.top - tooltipHeight;
      // "top" doesn't add a CSS class (unlike others) → might be intentional
      arrowX = anchorMiddleX - tooltipX - arrowWidth / 2;
      break;
  }

  // Translate tooltip and arrow relative to the constraint container
  // Division by 16 → converts px to rem (assuming root font-size = 16px)
  elTooltip.style.transform = `translate3d(${Math.round(tooltipX - scrollX - constraintRectangle.left) / 16}rem,${Math.round(tooltipY - scrollY - constraintRectangle.top) / 16}rem,0)`;
  elTooltipArrow.style.transform = `translate3d(${Math.round(arrowX) / 16}rem,${Math.round(arrowY) / 16}rem,0)`;
}
